import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspacePath } from '../../workspace-path';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C45 · a key in the reply is a link. `agent_plan_1.md` §11                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Minden hivatkozott entitás kattintható (mono kulcs → sheet)."* Until C27.7 the clickable path was
 * the data card — a real task table beside the reply, whose rows opened the overlay. The product
 * owner switched those off, and the reply became the only thing anybody sees: *"A HSO-131 két napot
 * csúszik"* as plain text, with the key that names the thing to look at inert.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THIS IS NOT THE DATA CARD COMING BACK, AND THE DIFFERENCE IS THE     │
 * │ ONE C17 REFUSED TO BLUR.                                                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A card renders **rows a service returned**; this renders **text the model wrote**. So nothing here
 * displays a figure, a title, a status or a date — it turns three shapes of identifier into an
 * address and leaves every character otherwise exactly as it arrived. The model cannot invent a fact
 * through this component, because it renders no facts: a key that does not exist becomes a link to a
 * screen that says so.
 *
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ INTERNAL ROUTES ONLY, AND *"WHY NOT AUTO-LINK URLS TOO"* HAS A       │
 * │ SECURITY ANSWER RATHER THAN A TASTE ONE.                                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The prose quotes workspace text written by people — a task title, a worklog note, a comment — and
 * the preamble is emphatic that such text is data rather than instructions. Auto-linking anything
 * that *looks* like a URL would hand that text an outbound click: somebody names a task
 * `https://evil.example/x` and the Agent's answer, in the product's own voice, renders a link to it.
 * These three patterns can only ever produce a path inside this app, so the surface does not exist.
 *
 * ⚠️ **No existence check, deliberately.** Verifying each key would be one request per key per
 * bubble, on a screen whose whole argument is that its figures came from a tool — and the failure it
 * would prevent is already handled: `/tasks/HSO-999` renders the not-found state
 * `state-rules.md` decides. A link that goes somewhere honest about being empty beats a paragraph
 * with a dead identifier in it.
 */

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ FOUR SHAPES, ONE PASS, AND THE CONTRACT ARM COMES FIRST.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * A project's key prefix is two to six capitals (`keyPrefixSchema`), so:
 *
 *  * `HSO-131` — a task. `/tasks/:key` is a first-class address (`GET /tasks/key/:key` behind it);
 *    ⚠️ J-7 · and so are `HSO-INT-131` (ours, in a prefix a tracker took) and `AUCHSUP2-9` (a
 *    synced task carries the tracker's key — up to ten characters, digits and underscores allowed).
 *    The task arm is `TASK_KEY_PATTERN` with its quantifiers bounded, upper-case only: a reply
 *    quoting a lower-case YouTrack key stays text, which is the honest fallback;
 *  * `HSO-C01` — a contract, whose code is `${prefix}-C${nn}` from `nextCode`;
 *  * `HSO-C01/2` — a line item, addressed by its contract's code and its own number (P2-9).
 *
 * ⚠️ **The contract alternative is written first because the alternation is ordered and the two
 * overlap.** With the task arm first, `HSO-C01` would not match it (`C01` is not digits) — but
 * `HSO-C01/2`'s trailing `/2` and a future code shape make the order worth pinning rather than
 * leaving to luck. Regex alternation takes the leftmost alternative that matches, so the more
 * specific one has to be on the left.
 *
 * ⚠️ **Bounded quantifiers everywhere.** A key is short; `\d+` on model output is a needless
 * invitation, and this expression runs on every character of every reply.
 *
 * ⚠️⚠️ **30 Oct 2026 · THE FOURTH SHAPE IS R-57's PUBLIC IDENTIFIER, AND ONLY THE FIVE WITH AN
 * ADDRESS.** The model reads and echoes `cont_12a3cd5a`, `lr_07bb0567`, `deal_0123abcd`, and since
 * R-57 _"the address is the identifier"_ — `/contracts/cont_…`, `/invoices/inv_…`, `/costs/ec_…`,
 * `/requests/lr_…`, `/deals/deal_…`. Those five link. `proj_…` and `member_…` stay text because
 * `/projects/:id` and `/people/:id` resolve a uuid and nothing else today (`ProjectService.one`,
 * `PersonService.one`); `li_…` stays text because a line item's address needs its contract beside
 * it. A slug outside the five is not matched at all, so an identifier the product has no page for
 * is never a link to a not-found screen. Lower-case only, as the database mints them.
 */
export const KEYS =
  /\b([A-Z]{2,6})-C(\d{1,4})(?:\/(\d{1,4}))?\b|\b([A-Z][A-Z0-9_]{1,9}(?:-INT)?)-(\d{1,6})\b|\b(cont|inv|ec|lr|deal)_([0-9a-f]{8})\b/g;

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE ADDRESS, IN ONE FUNCTION, BECAUSE THERE ARE NOW TWO CALLERS.     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `AgentProse` below splits a string into nodes; `agent-markdown.tsx` walks the text nodes of a
 * parsed tree (C48). Both need *"which screen does this identifier name"*, and a second copy of the
 * branch is a second place for `/contracts/HSO-C01/2` to be spelled — where the drift shows up as a
 * key that links to the right screen in a plain reply and the wrong one inside a bold run.
 *
 * ⚠️ **`null` for a match nobody understood**, rather than a thrown error or a non-null assertion.
 * It is unreachable while the expression has two arms and both are captured, and the honest fallback
 * for *"matched but not understood"* is the text left exactly as it arrived.
 *
 * ⚠️⚠️ **R-38 · IT RETURNS A WORKSPACE-RELATIVE PATH, AND THE PREFIX IS ADDED AT THE RENDER.** The
 * model writes `HSO-131`; the address is `/{workspace}/tasks/HSO-131`. The workspace is not this
 * function's to know — it is a pure string function with two callers and no router — so both
 * callers pass the result through `useWorkspacePath()`. Baking a workspace in here would make the
 * function's output depend on where it was called from, which is what a hook is for.
 */
/** R-57 · the screen each linkable slug names. A slug not in here is not in `KEYS` either. */
const IDENTIFIER_SCREENS: Readonly<Record<string, string>> = {
  cont: '/contracts',
  inv: '/invoices',
  ec: '/costs',
  lr: '/requests',
  deal: '/deals',
};

export function keyHref(match: RegExpMatchArray): string | null {
  const [, contractPrefix, contractNumber, lineItemNumber, taskPrefix, taskNumber, slug, hex] =
    match;

  if (slug !== undefined && hex !== undefined) {
    const screen = IDENTIFIER_SCREENS[slug];
    return screen === undefined ? null : `${screen}/${slug}_${hex}`;
  }

  if (contractPrefix !== undefined && contractNumber !== undefined) {
    const code = `${contractPrefix}-C${contractNumber}`;
    return lineItemNumber === undefined
      ? `/contracts/${code}`
      : `/contracts/${code}/${lineItemNumber}`;
  }

  if (taskPrefix !== undefined && taskNumber !== undefined) {
    return `/tasks/${taskPrefix}-${taskNumber}`;
  }

  return null;
}

/** ⚠️ Mono and `tabular-nums`, per §11: a key is `PROJ-nnn` in the product's monospace, everywhere. */
export const LINK = 'text-[12.5px] tabular-nums underline decoration-dotted hover:no-underline';

/**
 * The reply, with its identifiers clickable.
 *
 * ⚠️⚠️ **`whitespace-pre-line` SURVIVES BECAUSE NOTHING IS TRIMMED.** The model writes paragraphs and
 * the preamble forbids it markdown, so newlines are the only formatting that arrives — and this
 * splits the string without touching a character of it, so the caller's `whitespace-pre-line` still
 * has the newlines to render. A `.trim()` anywhere in here would silently reflow every reply.
 *
 * ⚠️ **A string, not a fragment, when nothing matches** — which is most short replies. `String.matchAll`
 * over a reply with no key allocates nothing and returns the text as it came.
 */
export function AgentProse({ text }: { readonly text: string }): ReactNode {
  /* ⚠️ R-38 · `keyHref` answers `/tasks/HSO-131`; the address needs the workspace segment. */
  const at_ = useWorkspacePath();
  const parts: ReactNode[] = [];
  let cursor = 0;

  for (const match of text.matchAll(KEYS)) {
    const at = match.index;
    const whole = match[0];

    if (at > cursor) parts.push(text.slice(cursor, at));
    cursor = at + whole.length;

    /* ⚠️ Which arm matched is decided by `keyHref` from the groups, not by a second test on the
     * text — and by the same function the markdown walker calls. */
    const href = keyHref(match);

    parts.push(
      href === null ? (
        whole
      ) : (
        <Link key={`${String(at)}-${whole}`} to={at_(href)} className={LINK}>
          {whole}
        </Link>
      ),
    );
  }

  if (parts.length === 0) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));

  return parts;
}
